import { useQuery } from '@tanstack/react-query';
import { paiementsApi } from '../services/api';
// `String(err)` affichait « ApiError: … » et perdait les `issues[]` d'une
// erreur Zod ; `text-red-600` est à ~4,26:1 sur blanc, sous le seuil de 4,5:1
// de la charte.
import { formatApiError } from '../lib/api-error';

/**
 * Module Paiements — écran « En cours de règlement » (Valérie / admin).
 * Paiements exportés (XML généré) en attente de dépôt, et payés.
 */

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const STATUS_LABEL: Record<string, string> = {
  exported: 'Exporté (à déposer)',
  paid: 'Payé',
};
const STATUS_BADGE: Record<string, string> = {
  exported: 'bg-blue-100 text-blue-800 ring-blue-200',
  paid: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};

export function PaiementsEnCoursPage() {
  const q = useQuery({
    queryKey: ['paiements', 'en-cours'],
    queryFn: () => paiementsApi.enCours(),
    staleTime: 30_000,
  });

  const rows = q.data?.paiements ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">En cours de règlement</h1>
        <p className="text-sm text-slate-500 mt-1">
          Paiements exportés en virement (fichier XML généré), en attente de dépôt bancaire puis payés.
        </p>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(q.error)}</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucun paiement en cours de règlement.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase">
              <tr>
                <th className="px-4 py-2 font-medium">Fournisseur</th>
                <th className="px-4 py-2 font-medium">N° facture</th>
                <th className="px-4 py-2 font-medium text-right">Montant</th>
                <th className="px-4 py-2 font-medium">Banque</th>
                <th className="px-4 py-2 font-medium">Fichier XML</th>
                <th className="px-4 py-2 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{p.vendor_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.bill_number}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{eur(p.montant)}</td>
                  <td className="px-4 py-2">{p.banque ?? '-'}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{p.batch?.xml_filename ?? '-'}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_BADGE[p.status] ?? 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
